import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Sidebar } from '../../components/Sidebar';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { UserPlus, Search, KeyRound, Trash2, Edit2, RefreshCw, Shield } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLES = [
  { value: 'brand',              label: 'Brand' },
  { value: 'agency',             label: 'Agency' },
  { value: 'creator',            label: 'Creator' },
  { value: 'account_executive',  label: 'Account Executive' },
  { value: 'campaign_manager',   label: 'Campaign Manager' },
  { value: 'talent_manager',     label: 'Talent Manager' },
  { value: 'admin',              label: 'Admin' },
];

// Filter dropdown excludes "Creator" — creators are managed via Roster/Discovery,
// not User Management. Create/Edit dialogs still allow `creator` as a role value.
const FILTER_ROLES = ROLES.filter((r) => r.value !== 'creator');

const ROLE_LABEL = Object.fromEntries(ROLES.map((r) => [r.value, r.label]));

const ROLE_COLOR = {
  admin: 'bg-[#FF451A]/15 text-[#FF451A] border-[#FF451A]/30',
  account_executive: 'bg-purple-500/15 text-purple-300 border-purple-500/30',
  campaign_manager: 'bg-blue-500/15 text-blue-300 border-blue-500/30',
  talent_manager: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  brand: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  agency: 'bg-sky-500/15 text-sky-300 border-sky-500/30',
  creator: 'bg-zinc-500/15 text-zinc-300 border-zinc-500/30',
};

const EMPTY_CREATE = { name: '', email: '', password: '', role: 'creator' };

export const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [q, setQ] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [activeFilter, setActiveFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_CREATE);
  const [creating, setCreating] = useState(false);

  // Edit dialog
  const [editUser, setEditUser] = useState(null); // user object or null
  const [editForm, setEditForm] = useState({ name: '', email: '', role: '', assigned_agency_id: '' });
  const [savingEdit, setSavingEdit] = useState(false);
  const [agencyOptions, setAgencyOptions] = useState([]); // for the brand → agency assignment dropdown

  // Reset password dialog
  const [pwUser, setPwUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [savingPw, setSavingPw] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (q.trim()) params.q = q.trim();
      if (roleFilter !== 'all') params.role = roleFilter;
      if (activeFilter !== 'all') params.active = activeFilter === 'active';
      const { data } = await axios.get(`${API_URL}/api/admin/users`, { params, withCredentials: true });
      setUsers(data.results || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, q, roleFilter, activeFilter]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const submitCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await axios.post(`${API_URL}/api/admin/users`, createForm, { withCredentials: true });
      toast.success(`${ROLE_LABEL[createForm.role] || 'User'} created`);
      setCreateOpen(false);
      setCreateForm(EMPTY_CREATE);
      setPage(1);
      fetchUsers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  const openEdit = async (u) => {
    setEditUser(u);
    setEditForm({
      name: u.name || '',
      email: u.email || '',
      role: u.role,
      assigned_agency_id: u.assigned_agency_id || '',
    });
    // Lazy-fetch the agency list only when editing a brand row.
    if (u.role === 'brand' && agencyOptions.length === 0) {
      try {
        const { data } = await axios.get(`${API_URL}/api/admin/users`, {
          params: { role: 'agency', page: 1, page_size: 200 },
          withCredentials: true,
        });
        setAgencyOptions(data.results || []);
      } catch (_e) { /* silent — dropdown will just be empty */ }
    }
  };

  const submitEdit = async () => {
    if (!editUser) return;
    setSavingEdit(true);
    try {
      const payload = {};
      if (editForm.name !== editUser.name) payload.name = editForm.name;
      if (editForm.email !== editUser.email) payload.email = editForm.email;
      if (editForm.role !== editUser.role) payload.role = editForm.role;
      const originalAgency = editUser.assigned_agency_id || '';
      if (editForm.assigned_agency_id !== originalAgency) {
        payload.assigned_agency_id = editForm.assigned_agency_id || null;
      }
      if (Object.keys(payload).length === 0) {
        toast.message('Nothing to save');
        setEditUser(null);
        return;
      }
      await axios.patch(`${API_URL}/api/admin/users/${editUser.id}`, payload, { withCredentials: true });
      toast.success('User updated');
      setEditUser(null);
      fetchUsers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update user');
    } finally {
      setSavingEdit(false);
    }
  };

  const submitPw = async () => {
    if (!pwUser) return;
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setSavingPw(true);
    try {
      await axios.patch(
        `${API_URL}/api/admin/users/${pwUser.id}`,
        { password: newPassword },
        { withCredentials: true }
      );
      toast.success(`Password reset for ${pwUser.email}`);
      setPwUser(null);
      setNewPassword('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to reset password');
    } finally {
      setSavingPw(false);
    }
  };

  const toggleActive = async (u) => {
    const next = !(u.active !== false);
    try {
      if (!next) {
        // Confirm soft delete
        if (!window.confirm(`Deactivate ${u.email}? They will be unable to log in until reactivated.`)) return;
        await axios.delete(`${API_URL}/api/admin/users/${u.id}`, { withCredentials: true });
        toast.success('User deactivated');
      } else {
        await axios.patch(`${API_URL}/api/admin/users/${u.id}`, { active: true }, { withCredentials: true });
        toast.success('User reactivated');
      }
      fetchUsers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to toggle status');
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="flex min-h-screen bg-white dark:bg-[#0A0B0D]">
      <Sidebar />

      <div className="flex-1 ml-64 p-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-start justify-between mb-8" data-testid="admin-users-header">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Shield size={14} className="text-[#FF451A]" />
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#6B6B6B] dark:text-zinc-500">Admin · Internal</span>
              </div>
              <h1 className="font-display text-4xl text-[#0F0F0F] dark:text-white tracking-tight">User Management</h1>
              <p className="text-sm text-[#6B6B6B] dark:text-zinc-500 mt-2">
                Create, edit, deactivate, and reset passwords for every user on the platform.
              </p>
            </div>
            <Button
              onClick={() => setCreateOpen(true)}
              className="bg-[#FF451A] hover:bg-[#FF451A]/90 text-[#0F0F0F] dark:text-white"
              data-testid="admin-users-create-btn"
            >
              <UserPlus size={16} className="mr-2" /> Create User
            </Button>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 mb-6">
            <div className="md:col-span-6 relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6B6B] dark:text-zinc-500" />
              <Input
                value={q}
                onChange={(e) => { setQ(e.target.value); setPage(1); }}
                placeholder="Search by email or name…"
                className="pl-9 h-10 bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
                data-testid="admin-users-search"
              />
            </div>
            <div className="md:col-span-3">
              <Select value={roleFilter} onValueChange={(v) => { setRoleFilter(v); setPage(1); }}>
                <SelectTrigger className="h-10 bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500" data-testid="admin-users-role-filter">
                  <SelectValue placeholder="All roles" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All roles</SelectItem>
                  {FILTER_ROLES.map((r) => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Select value={activeFilter} onValueChange={(v) => { setActiveFilter(v); setPage(1); }}>
                <SelectTrigger className="h-10 bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500" data-testid="admin-users-active-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Deactivated</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-1">
              <Button
                variant="ghost"
                onClick={fetchUsers}
                disabled={loading}
                className="w-full text-[#6B6B6B] dark:text-zinc-400 hover:text-white h-10"
                data-testid="admin-users-refresh"
              >
                <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
              </Button>
            </div>
          </div>

          {/* Table */}
          <div className="bg-white/[0.02] border border-black/[0.10] dark:border-white/10 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-white/[0.03]">
                  <tr className="text-left font-mono text-[10px] uppercase tracking-[0.15em] text-[#6B6B6B] dark:text-zinc-500">
                    <th className="p-4">Name</th>
                    <th className="p-4">Email</th>
                    <th className="p-4">Role</th>
                    <th className="p-4">Status</th>
                    <th className="p-4">Created</th>
                    <th className="p-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={6} className="p-8 text-center text-[#6B6B6B] dark:text-zinc-500">Loading…</td></tr>
                  ) : users.length === 0 ? (
                    <tr><td colSpan={6} className="p-8 text-center text-[#6B6B6B] dark:text-zinc-500">No users found</td></tr>
                  ) : users.map((u) => {
                    const isActive = u.active !== false;
                    return (
                      <tr key={u.id} className="border-t border-black/[0.08] dark:border-white/5 hover:bg-white/[0.02] transition-colors" data-testid={`user-row-${u.id}`}>
                        <td className="p-4 text-[#0F0F0F] dark:text-white">{u.name || '—'}</td>
                        <td className="p-4 text-zinc-300">{u.email}</td>
                        <td className="p-4">
                          <span className={`inline-flex items-center px-2 py-1 rounded-md border text-[11px] font-mono uppercase tracking-wider ${ROLE_COLOR[u.role] || 'bg-white/5 text-[#6B6B6B] dark:text-zinc-400 border-black/[0.10] dark:border-white/10'}`} data-testid={`user-role-${u.id}`}>
                            {ROLE_LABEL[u.role] || u.role}
                          </span>
                        </td>
                        <td className="p-4">
                          <span className={`inline-flex items-center gap-1.5 text-xs ${isActive ? 'text-emerald-400' : 'text-[#6B6B6B] dark:text-zinc-500'}`} data-testid={`user-status-${u.id}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-emerald-400' : 'bg-zinc-600'}`} />
                            {isActive ? 'Active' : 'Deactivated'}
                          </span>
                        </td>
                        <td className="p-4 text-[#6B6B6B] dark:text-zinc-500 text-xs">
                          {u.created_at ? String(u.created_at).slice(0, 10) : '—'}
                        </td>
                        <td className="p-4">
                          <div className="flex items-center justify-end gap-1">
                            <Button size="sm" variant="ghost" onClick={() => openEdit(u)} className="text-[#6B6B6B] dark:text-zinc-400 hover:text-white" data-testid={`user-edit-${u.id}`}>
                              <Edit2 size={14} />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => { setPwUser(u); setNewPassword(''); }} className="text-[#6B6B6B] dark:text-zinc-400 hover:text-white" data-testid={`user-reset-pw-${u.id}`}>
                              <KeyRound size={14} />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => toggleActive(u)} className={isActive ? 'text-[#6B6B6B] dark:text-zinc-400 hover:text-red-400' : 'text-emerald-400 hover:text-emerald-300'} data-testid={`user-toggle-active-${u.id}`}>
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination footer */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-black/[0.08] dark:border-white/5 text-xs text-[#6B6B6B] dark:text-zinc-500">
              <span>{total.toLocaleString()} total · page {page} of {totalPages}</span>
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" disabled={page <= 1 || loading} onClick={() => setPage(page - 1)} className="text-zinc-300 hover:text-white">
                  Previous
                </Button>
                <Button size="sm" variant="ghost" disabled={page >= totalPages || loading} onClick={() => setPage(page + 1)} className="text-zinc-300 hover:text-white">
                  Next
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={(o) => { if (!creating) setCreateOpen(o); }}>
        <DialogContent className="bg-white dark:bg-[#0A0B0D] border border-black/[0.10] dark:border-white/10 text-[#0F0F0F] dark:text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Create User</DialogTitle>
          <DialogDescription className="sr-only">Dialog content — use the controls below to complete the action.</DialogDescription>
            <p className="text-xs text-[#6B6B6B] dark:text-zinc-500 mt-1">Internal staff accounts can only be created from this screen.</p>
          </DialogHeader>
          <form onSubmit={submitCreate} className="space-y-4 mt-2">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] block mb-2 text-[#0F0F0F] dark:text-white">Name</label>
              <Input value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} required className="bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500" data-testid="create-user-name" />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] block mb-2 text-[#0F0F0F] dark:text-white">Email</label>
              <Input type="email" value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} required className="bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500" data-testid="create-user-email" />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] block mb-2 text-[#0F0F0F] dark:text-white">Temporary Password</label>
              <Input type="text" value={createForm.password} onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })} required minLength={6} className="bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500" data-testid="create-user-password" />
              <p className="text-[10px] text-[#A1A1AA] dark:text-zinc-600 mt-1">Min 6 characters. Share with user securely.</p>
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] block mb-2 text-[#0F0F0F] dark:text-white">Role</label>
              <Select value={createForm.role} onValueChange={(v) => setCreateForm({ ...createForm, role: v })}>
                <SelectTrigger className="bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500" data-testid="create-user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setCreateOpen(false)} disabled={creating} className="text-[#6B6B6B] dark:text-zinc-400">
                Cancel
              </Button>
              <Button type="submit" disabled={creating} className="bg-[#FF451A] hover:bg-[#FF451A]/90 text-[#0F0F0F] dark:text-white" data-testid="create-user-submit">
                {creating ? 'Creating…' : 'Create User'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editUser} onOpenChange={(o) => { if (!savingEdit && !o) setEditUser(null); }}>
        <DialogContent className="bg-white dark:bg-[#0A0B0D] border border-black/[0.10] dark:border-white/10 text-[#0F0F0F] dark:text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Edit User</DialogTitle>
          <DialogDescription className="sr-only">Dialog content — use the controls below to complete the action.</DialogDescription>
            <p className="text-xs text-[#6B6B6B] dark:text-zinc-500 mt-1">{editUser?.email}</p>
          </DialogHeader>
          {editUser && (
            <div className="space-y-4 mt-2">
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] block mb-2 text-[#0F0F0F] dark:text-white">Name</label>
                <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500" data-testid="edit-user-name" />
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] block mb-2 text-[#0F0F0F] dark:text-white">Email</label>
                <Input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} className="bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500" data-testid="edit-user-email" />
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] block mb-2 text-[#0F0F0F] dark:text-white">Role</label>
                <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })}>
                  <SelectTrigger className="bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500" data-testid="edit-user-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map((r) => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {editForm.role === 'brand' && (
                <div data-testid="edit-user-assigned-agency-wrap">
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] block mb-2 text-[#0F0F0F] dark:text-white">
                    Assigned Agency <span className="text-[#6B6B6B] dark:text-zinc-600 normal-case tracking-normal">(optional)</span>
                  </label>
                  <Select
                    value={editForm.assigned_agency_id || 'none'}
                    onValueChange={(v) => setEditForm({ ...editForm, assigned_agency_id: v === 'none' ? '' : v })}
                  >
                    <SelectTrigger className="bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500" data-testid="edit-user-assigned-agency">
                      <SelectValue placeholder="Not assigned" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Not assigned</SelectItem>
                      {agencyOptions.map((a) => (
                        <SelectItem key={a.id} value={a.id}>
                          {a.company_name || a.name} · {a.email}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {agencyOptions.length === 0 && (
                    <p className="text-[10px] text-[#6B6B6B] dark:text-zinc-500 mt-1.5">
                      No agency accounts exist yet. Create one via <b>+ New User</b> with role <b>Agency</b>.
                    </p>
                  )}
                </div>
              )}
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setEditUser(null)} disabled={savingEdit} className="text-[#6B6B6B] dark:text-zinc-400">
                  Cancel
                </Button>
                <Button onClick={submitEdit} disabled={savingEdit} className="bg-[#FF451A] hover:bg-[#FF451A]/90 text-[#0F0F0F] dark:text-white" data-testid="edit-user-save">
                  {savingEdit ? 'Saving…' : 'Save Changes'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={!!pwUser} onOpenChange={(o) => { if (!savingPw && !o) setPwUser(null); }}>
        <DialogContent className="bg-white dark:bg-[#0A0B0D] border border-black/[0.10] dark:border-white/10 text-[#0F0F0F] dark:text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Reset Password</DialogTitle>
          <DialogDescription className="sr-only">Dialog content — use the controls below to complete the action.</DialogDescription>
            <p className="text-xs text-[#6B6B6B] dark:text-zinc-500 mt-1">{pwUser?.email}</p>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] block mb-2 text-[#0F0F0F] dark:text-white">New Password</label>
              <Input type="text" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} minLength={6} className="bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500" data-testid="reset-pw-input" />
              <p className="text-[10px] text-[#A1A1AA] dark:text-zinc-600 mt-1">Min 6 characters. Share with user securely.</p>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setPwUser(null)} disabled={savingPw} className="text-[#6B6B6B] dark:text-zinc-400">
                Cancel
              </Button>
              <Button onClick={submitPw} disabled={savingPw} className="bg-[#FF451A] hover:bg-[#FF451A]/90 text-[#0F0F0F] dark:text-white" data-testid="reset-pw-submit">
                {savingPw ? 'Saving…' : 'Reset Password'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
